import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setDealerUserPassword } from '../../services/dealerUserService'
import { apiError } from '../../api/apiError'
import type { DealerUser } from '../../types'
import '../BranchForm/BranchFormModal.css'

interface Props {
  branchId: string
  user: DealerUser
  onClose: () => void
  onSaved: (u: DealerUser) => void
}

const MIN_LENGTH = 8
// без похожих символов (0/O, 1/l/I), чтобы пароль было легко продиктовать
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'

function generatePassword(length = 12): string {
  const bytes = new Uint32Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join('')
}

export default function DealerUserPasswordModal({ branchId, user, onClose, onSaved }: Props) {
  const { t } = useTranslation()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  function handleGenerate() {
    const generated = generatePassword()
    setPassword(generated)
    setConfirm(generated)
    setVisible(true)
    setErrors({})
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (password.length < MIN_LENGTH) errs.password = t('admin.dealer_users.password_too_short')
    else if (password !== confirm) errs.confirm = t('admin.dealer_users.password_mismatch')
    setErrors(errs)
    if (Object.keys(errs).length) return

    setLoading(true)
    try {
      onSaved(await setDealerUserPassword(branchId, user.id, password))
    } catch (err) {
      const { code, message } = apiError(err)
      if (code === 'PASSWORD_TOO_SHORT') setErrors({ password: t('admin.dealer_users.password_too_short') })
      else if (code === 'USER_DISABLED') setErrors({ _: t('admin.dealer_users.error_user_disabled') })
      else setErrors({ _: message || t('admin.dealer_users.action_failed') })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{t('admin.dealer_users.password_modal_title')}</h2>
          <button className="modal__close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form className="modal__body" onSubmit={handleSubmit} noValidate>
          <p className="dealer-users__hint">{user.email}</p>

          <div className="modal__field">
            <label className="modal__label" htmlFor="dealer-user-password">{t('admin.dealer_users.label_password')}</label>
            <input
              id="dealer-user-password"
              className={`admin-input modal__input${errors.password ? ' admin-input--error' : ''}`}
              type={visible ? 'text' : 'password'}
              value={password}
              autoComplete="new-password"
              onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: '' })) }}
              disabled={loading}
              autoFocus
            />
            {errors.password && <span className="modal__error">{errors.password}</span>}
          </div>

          <div className="modal__field">
            <label className="modal__label" htmlFor="dealer-user-password-confirm">{t('admin.dealer_users.label_password_confirm')}</label>
            <input
              id="dealer-user-password-confirm"
              className={`admin-input modal__input${errors.confirm ? ' admin-input--error' : ''}`}
              type={visible ? 'text' : 'password'}
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => { setConfirm(e.target.value); setErrors((p) => ({ ...p, confirm: '' })) }}
              disabled={loading}
            />
            {errors.confirm && <span className="modal__error">{errors.confirm}</span>}
          </div>

          <div className="dealer-users__password-tools">
            <button type="button" className="admin-btn admin-btn--secondary admin-btn--xs" onClick={handleGenerate} disabled={loading}>
              {t('admin.dealer_users.generate_password')}
            </button>
            <button type="button" className="admin-btn admin-btn--secondary admin-btn--xs" onClick={() => setVisible((v) => !v)} disabled={loading}>
              {visible ? t('admin.dealer_users.hide_password') : t('admin.dealer_users.show_password')}
            </button>
          </div>

          <p className="dealer-users__hint">{t('admin.dealer_users.password_hint')}</p>

          {errors._ && <p className="modal__error">{errors._}</p>}

          <div className="modal__footer">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={onClose} disabled={loading}>
              {t('admin.dealer_users.cancel')}
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
              {loading ? t('admin.dealer_users.saving') : t('admin.dealer_users.save_password')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
